import { Core } from "@faire/design-tokens";
import { AppDownloadCTA } from "@faire/retailer-visitor-shared/components/Footer/AppDownloadCTA";
import {
  DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING,
  MOBILE_FOOTER_HORIZ_PADDING,
  TABLET_FOOTER_HORIZ_PADDING,
} from "@faire/retailer-visitor-shared/components/Footer/consts";
import { FooterBottom } from "@faire/retailer-visitor-shared/components/Footer/FooterBottom";
import { LoggedOutAppDownloadCTA } from "@faire/retailer-visitor-shared/components/Footer/LoggedOutAppDownloadCTA";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useShowContextualAppDownloadCTAs } from "@faire/retailer-visitor-shared/hooks/retailer/useShowContextualAppDownloadCTAs";
import {
  AccessibilityAnchor,
  AccessibilityLink,
} from "@faire/retailer-visitor-shared/lib/AccessibilityLink";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useIsSemDiscoverPage } from "@faire/retailer-visitor-shared/lib/useIsSemDiscoverPage";
import { blogPage } from "@faire/retailer-visitor-shared/paths/index";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { getSettingOpenWithFaireAvailableForCountry } from "@faire/retailer-visitor-shared/settings/getSettingOpenWithFaireAvailableForCountry";
import {
  useAssignSettingLowLiftNewFooterXP,
  useSettingLowLiftNewFooterXP,
} from "@faire/retailer-visitor-shared/settings/useSettingLowLiftNewFooterXP";
import { useSettingOwFGrant } from "@faire/retailer-visitor-shared/settings/useSettingOwFGrant";
import { SOCIAL } from "@faire/web--source/common/consts/SOCIAL";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useLocalization } from "@faire/web--source/common/localization/useLocalization";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Badge } from "@faire/web--source/slate/deprecated/Badge";
import {
  Column,
  Flex,
  HorizontalLine,
  Row,
} from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { trackHelpCenterFaireHomeFooterHelpCenterClick } from "@faire/web-api--source/events/helpCenter/click/faireHomeFooterHelpCenter";
import { trackLoggedOutRetailersFooterLinksActionCategoryClick } from "@faire/web-api--source/events/loggedOutRetailers/actionCategoryClick/footerLinks";
import { trackLoggedOutRetailersHomePageFooterInsiderLabelClick } from "@faire/web-api--source/events/loggedOutRetailers/click/homePageFooterInsiderLabel";
import affiliates from "@faire/web-api--source/routes/www/affiliates";
import brands from "@faire/web-api--source/routes/www/brands";
import careers from "@faire/web-api--source/routes/www/careers";
import insiderMarketingPage from "@faire/web-api--source/routes/www/insider";
import faireMarkets from "@faire/web-api--source/routes/www/markets";
import posAbout from "@faire/web-api--source/routes/www/point-of-sale/about";
import support from "@faire/web-api--source/routes/www/support";
import supportRetailers from "@faire/web-api--source/routes/www/support/retailers";
import * as React from "react";
import styled from "styled-components";

import { Batch8NewFooter } from "./batch8NewFooter/Batch8NewFooter";

interface IPropTypes {
  className?: string;
  noTopBorder?: boolean;
  noTopMargin?: boolean;
  childFirst?: boolean;
  children?: React.ReactNode;
  hideReferral?: boolean;
  showRetailerFooterForNewFooterXP?: boolean;
  isNewsroomPage?: boolean;
}

interface IFooterLink {
  name: string;
  to: string;
  target: "_blank" | undefined;
  isExternal: boolean;
  shouldShow?: boolean;
  eventPath?: string;
  isNew?: boolean;
  peteEvent?: () => void;
}

export interface IFooterSection {
  title: string;
  links: IFooterLink[];
}

const OriginalFooter = ({
  ref,
  ...props
}: IPropTypes & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const {
    className = "",
    noTopBorder = false,
    noTopMargin = false,
    childFirst = false,
    children,
  } = props;

  const { strictLocalize } = useStrictLocalization();
  const retailerToken = getRetailer()?.token;
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isSemDiscoverPage = useIsSemDiscoverPage();
  const {
    intl: { locale },
  } = useLocalization();

  const { hasAccessToLoggedOutInsiderMarketingPage } =
    useInsiderFeatureEligibility();

  const isEligibleForOwFGrant = useSettingOwFGrant();
  const { showContextualAppDownloadCTAs } = useShowContextualAppDownloadCTAs();

  const footerSections: IFooterSection[] = [
    {
      title: strictLocalize({
        defaultMessage: "Company",
        description: { text: "group of links to pages related to Faire" },
      }),
      links: [
        {
          name: strictLocalize({
            defaultMessage: "About us",
            description: {
              text: "Label on a button that takes you to the about us page",
              img: "https://cdn.faire.com/fastly/93eef6e47ce10140b20f33493b3b79691426127c350fc9d715ddc68cebaba1a8.png",
            },
          }),
          to: "/about",
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({ defaultMessage: "Newsroom" }),
          to: "https://news.faire.com",
          target: undefined,
          isExternal: true,
          shouldShow: true,
        },
        {
          name: strictLocalize({ defaultMessage: "Careers" }),
          to: careers(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            defaultMessage: "Affiliates",
            description: {
              text: "Link to the affiliates page",
              img: "https://cdn.faire.com/fastly/c2f423a100c79716eb5efb6b965b654483b9addb7b2c15c02c50d0726f27a16c.png",
            },
          }),
          to: affiliates(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
      ],
    },
    {
      title: strictLocalize({
        defaultMessage: "Explore",
        description: {
          text: "Group of Faire resource links to explore",
          img: "https://cdn.faire.com/fastly/527ce0c2c2f77983f781fe71151c77fd50ba2468c4f78d66a03bcc143d4f68f8.png",
        },
      }),
      links: [
        {
          name: strictLocalize({
            defaultMessage: "Help center",
            description: {
              text: "Label on a button that takes you to the help center page",
              img: "https://cdn.faire.com/fastly/93eef6e47ce10140b20f33493b3b79691426127c350fc9d715ddc68cebaba1a8.png",
            },
          }),
          to: isLoggedInRetailer ? supportRetailers() : support(),
          target: "_blank",
          isExternal: false,
          shouldShow: true,
          peteEvent: () =>
            trackHelpCenterFaireHomeFooterHelpCenterClick(
              retailerToken ?? "",
              ""
            ),
        },
        {
          name: strictLocalize({ defaultMessage: "Open with Faire" }),
          to: "/open-with-faire",
          target: "_blank",
          isExternal: false,
          shouldShow: getSettingOpenWithFaireAvailableForCountry(),
          eventPath: "/open-with-faire/general-footer/click",
        },
        {
          name: strictLocalize({
            defaultMessage: "Faire Markets",
            description: {
              text: "Link to faire markets page which gives an overview of what faire markets are, and provides links to learn more about past events",
            },
          }),
          to: faireMarkets(),
          target: "_blank",
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            defaultMessage: "Sell on Faire",
            description: {
              text: "Label on a button to apply as a brand",
            },
          }),
          to: brands(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
        {
          name: strictLocalize({
            defaultMessage: "Small Business Grant",
            description: {
              text: "Label on a button that takes you to the small business grant page",
            },
          }),
          to: "/small-business-grant",
          target: undefined,
          isExternal: false,
          shouldShow: isEligibleForOwFGrant,
        },
        {
          name: strictLocalize({
            defaultMessage: "Insider",
            description: {
              text: "Label on a button that takes you to the insider marketing page",
              img: "https://cdn.faire.com/fastly/3ce05e0d8114e6dd90a8ef89787032930aa0b07debd8e0c80a954d798bd50aa3.png",
            },
          }),
          to: insiderMarketingPage(),
          target: "_blank",
          isExternal: false,
          shouldShow:
            !isLoggedInRetailer && hasAccessToLoggedOutInsiderMarketingPage,
          peteEvent: () =>
            trackLoggedOutRetailersHomePageFooterInsiderLabelClick(),
        },
        {
          name: strictLocalize({
            defaultMessage: "POS integration",
            description: {
              text: "Link to the POS integrations marketing page",
              img: "https://cdn.faire.com/fastly/8b21891886b265c21e7e062892d2b9524ca29f98f7a80c90c0a877efe1ab405a.png",
            },
          }),
          to: posAbout(),
          target: undefined,
          isExternal: false,
          shouldShow: true,
        },
      ],
    },
    {
      title: strictLocalize({
        defaultMessage: "Connect",
        description: { text: "group of Faire social media links" },
      }),
      links: [
        {
          name: strictLocalize({ defaultMessage: "Blog" }),
          to: `${blogPage(locale)}/`,
          target: "_blank",
          isExternal: true,
          shouldShow: true,
        },
        {
          name: strictLocalize({ defaultMessage: "Instagram" }),
          to: SOCIAL.INSTAGRAM,
          target: "_blank",
          isExternal: true,
          shouldShow: true,
        },
        {
          name: strictLocalize({ defaultMessage: "Facebook" }),
          to: SOCIAL.FACEBOOK,
          target: "_blank",
          isExternal: true,
          shouldShow: true,
        },
        {
          name: strictLocalize({ defaultMessage: "Twitter" }),
          to: SOCIAL.TWITTER,
          target: "_blank",
          isExternal: true,
          shouldShow: true,
        },
      ],
    },
  ];

  const LoggedInOrLoggedOutAppDownloadCTA =
    isLoggedInRetailer && showContextualAppDownloadCTAs
      ? AppDownloadCTA
      : LoggedOutAppDownloadCTA;

  if (isSemDiscoverPage) {
    return null;
  }

  return (
    <ZIndexContainer ref={ref} className={className}>
      {noTopBorder ? null : <HorizontalLine />}
      <FooterContainer $noTopMargin={noTopMargin}>
        <ContentWrapper>
          <FooterSectionsWrapper>
            <Content>
              {footerSections.map((section, idx) => (
                <FooterSection
                  key={section.title}
                  section={section}
                  hideDivider={idx === 0}
                />
              ))}
            </Content>
          </FooterSectionsWrapper>
          <Children $childFirst={childFirst}>
            {children ? children : <LoggedInOrLoggedOutAppDownloadCTA />}
          </Children>
        </ContentWrapper>
      </FooterContainer>
      <HorizontalLine />
      <FooterBottom />
    </ZIndexContainer>
  );
};

export const Footer: React.FC<
  IPropTypes & {
    ref?: React.Ref<HTMLDivElement>;
  }
> = (props) => {
  const assignSettingLowLiftNewFooterXP = useAssignSettingLowLiftNewFooterXP();
  const lowLiftNewFooterXP = useSettingLowLiftNewFooterXP();

  const assignRef = useDoActionWhenVisible(() => {
    if (!props.isNewsroomPage) {
      assignSettingLowLiftNewFooterXP();
    }
  });

  return (
    <FooterWrapper ref={assignRef}>
      {lowLiftNewFooterXP || props.isNewsroomPage ? (
        <Batch8NewFooter {...props} />
      ) : (
        <OriginalFooter {...props} />
      )}
    </FooterWrapper>
  );
};

const FooterSection: React.FC<{
  section: IFooterSection;
  hideDivider: boolean;
}> = ({ section, hideDivider }) => {
  return (
    <React.Fragment key={section.title}>
      <HorizontalSectionLine $hidden={hideDivider} />
      <Section>
        <SectionTitle>{section.title}</SectionTitle>
        <SectionLinks>
          {section.links.map((link) => (
            <SectionLink key={link.name} link={link} />
          ))}
        </SectionLinks>
      </Section>
    </React.Fragment>
  );
};

const FooterSectionsWrapper = styled.div`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

export const SectionLink: React.FC<{
  link: IFooterLink;
}> = ({ link }) => {
  if (!link.shouldShow) {
    return null;
  }

  const handleLinkClick = () => {
    if (link.peteEvent) {
      link.peteEvent();
    }
    if (link.eventPath) {
      trackEventOnClick(link.eventPath);
    }

    trackLoggedOutRetailersFooterLinksActionCategoryClick(link.to);
  };

  if (link.isExternal) {
    return (
      <SectionItem key={link.name}>
        <AccessibilityAnchor
          href={link.to}
          target={link.target}
          rel="noopener noreferrer"
          onClick={handleLinkClick}
        >
          <LinkText>{link.name}</LinkText>
        </AccessibilityAnchor>
      </SectionItem>
    );
  }

  return (
    <SectionItem key={link.name}>
      <Row align="center">
        <AccessibilityLink
          to={link.to}
          target={link.target}
          onClick={handleLinkClick}
        >
          <LinkText>{link.name}</LinkText>
        </AccessibilityLink>
        {link.isNew ? (
          <>
            <Spacer size="1x" />
            <NewBadge content="New" backgroundColor={Core.surface.success} />
          </>
        ) : null}
      </Row>
    </SectionItem>
  );
};

const FooterWrapper = styled.div`
  width: 100%;
`;

const ZIndexContainer = styled.div`
  position: relative;
  width: 100%;
  background-color: ${Core.surface.inverse};
`;

const FooterContainer = styled.footer<{
  $noTopMargin?: boolean;
}>`
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  max-width: 1440px;
  padding: 0 ${MOBILE_FOOTER_HORIZ_PADDING}px;
  margin: 0 auto;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 0 ${TABLET_FOOTER_HORIZ_PADDING}px;
    margin: ${(props) => (props.$noTopMargin ? "0" : "56px")} auto 48px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 0 ${DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING}px;
  }
`;

const ContentWrapper = styled(Flex).attrs({
  justify: { mobileAndAbove: "flex-start", tabletAndAbove: "space-between" },
  align: "flex-start",
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;

const Content = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    margin: 24px 0;
    flex-direction: row;
    width: auto;
  }
`;

const HorizontalSectionLine = styled(HorizontalLine)<{ $hidden: boolean }>`
  display: ${(props) => (props.$hidden ? "none" : "block")};

  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;

const Section = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-right: 30px;
  width: 100%;
  padding: 16px 0;

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 0;
    margin-right: 50px;
  }
`;

const SectionTitle = styled(Typography).attrs({
  variant: "paragraphSansMedium",
})`
  &&& {
    margin: 0 0 6px;
  }
`;

const LINK_MARGIN = 3;
const SectionLinks = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin: ${-LINK_MARGIN}px 0;
`;

const SectionItem = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin: ${LINK_MARGIN}px 0;
`;

const LinkText = styled(Typography)`
  white-space: nowrap;
`;

const Children = styled(Column)<{ $childFirst: boolean }>`
  align-self: stretch;
  order: ${(props) => (props.$childFirst ? "-1" : "0")};
  margin: 0 ${-MOBILE_FOOTER_HORIZ_PADDING}px;

  ${responsiveMediaQueries.tablet} {
    margin: 0
      ${(props) => (props.$childFirst ? 0 : -TABLET_FOOTER_HORIZ_PADDING)}px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    margin: 0
      ${(props) =>
        props.$childFirst ? 0 : -DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING}px;
  }
`;

const NewBadge = styled(Badge)`
  width: 38px;
  height: 24px;
`;
